import EmojiPicker from "emoji-picker-react";
import { useContext, useEffect, useRef } from "react";
import { currentRoute, LoginContext } from "../../contexts/global/LoginContext";
import { SocketContext } from "../../contexts/global/SocketContext";
import { AttendancesContext } from "../../contexts/user/AttendancesContext";
import { ChatContext } from "../../contexts/user/ChatContext";
import { CustomState } from "../../Hooks/State.hook";
import { AttendanceMessage, SpecificAttendanceDetails } from "../../interfaces/Attendances.interfaces";
import { FileSendForm } from "./FileSendForm";
import { Message } from "./Message";
import { StyledAttendanceChat } from "./style";
import { TypeMessageForm } from "./TypeMessageForm";

interface Props {
    attendance: SpecificAttendanceDetails;
    desactivateChat?: boolean;
};

export const AttendanceChat = ({ attendance, desactivateChat }: Props) => {
    const { user } = useContext(LoginContext);
    const isChatDesactivated: boolean = desactivateChat || false;

    const {
        isEmotePickerActive,
        handleDrop,
        handleDragOver,
        onEmojiClick,
        handlePaste,
        formData
    } = useContext(ChatContext);

    const { attendancesMessagesGrouped } = useContext(AttendancesContext);
    const { socket } = useContext(SocketContext);
    const readingAttendanceMessages = CustomState<Array<AttendanceMessage>>([]);

    useEffect(() => {
        const messagesGrouped = attendancesMessagesGrouped.value
        let messages = messagesGrouped[attendance.CODIGO_NUMERO] ? messagesGrouped[attendance.CODIGO_NUMERO] : [];


        if (user && currentRoute === "vollo" && user.SETOR === 15) {
            messages = messages.filter(m => [-5000, 10].includes(m.CODIGO_OPERADOR));
        }

        const uniqueCodigos = new Set();
        const filteredByCodigo = messages.filter((message) => {
            if (!uniqueCodigos.has(message.CODIGO)) {
                uniqueCodigos.add(message.CODIGO);
                return true;
            }
            return false;
        });

        const sortedMessages = filteredByCodigo.sort((a, b) => new Date(a.TIMESTAMP).getTime() - new Date(b.TIMESTAMP).getTime())
        readingAttendanceMessages.set(sortedMessages);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attendance, attendancesMessagesGrouped.value]);

    const ulRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
        if (ulRef.current) {
            ulRef.current.scrollTop = ulRef.current.scrollHeight;
        }

        if (socket.value) {
            socket.value.emit("read-attendance-messages", {
                attendanceId: attendance.CODIGO_NUMERO,
                clientName: currentRoute
            });

            const oldMessages = attendancesMessagesGrouped.value[attendance.CODIGO_NUMERO] ? attendancesMessagesGrouped.value[attendance.CODIGO_NUMERO] : [];
            const notReadMessages = oldMessages.filter(n => !n.FROM_ME && n.STATUS !== "READ" && readingAttendanceMessages.value.some(m => m.CODIGO === n.CODIGO));

            if (notReadMessages.length) {
                for (const i in oldMessages) {
                    if (
                        readingAttendanceMessages.value.some(m => m.CODIGO === oldMessages[i].CODIGO) &&
                        !oldMessages[i].FROM_ME &&
                        oldMessages[i].STATUS !== "READ"
                    ) {
                        oldMessages[i].STATUS = "READ";
                    }
                }
                attendancesMessagesGrouped.set((prev) => {
                    return { ...prev, [attendance.CODIGO_NUMERO]: oldMessages }
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readingAttendanceMessages.value, socket.value]);

    const dedupedMessages = Array.from(new Map(readingAttendanceMessages.value.map(item => [item.CODIGO, item])).values());

    return (
        <>
            <StyledAttendanceChat
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onPaste={handlePaste}
            >
                {
                    !isChatDesactivated &&
                        (formData.value.file && !formData.value.isAudio) ?
                        <FileSendForm attendance={attendance} />
                        :
                        <>
                            <ul ref={ulRef} >
                                {
                                    attendance &&
                                    dedupedMessages.map((m) => <Message message={m} key={m.CODIGO} />)
                                }
                            </ul>
                            {
                                !isChatDesactivated &&
                                isEmotePickerActive &&
                                <EmojiPicker
                                    width="calc(100% - 0.25rem)"
                                    height="70vh"
                                    previewConfig={{
                                        showPreview: false
                                    }}
                                    onEmojiClick={onEmojiClick}
                                />
                            }
                            {
                                !isChatDesactivated && attendance.CONTATO_NUMERO &&
                                <TypeMessageForm attendance={attendance} />
                            }
                        </>
                }
            </StyledAttendanceChat>
        </>
    );
};